import { Injectable, computed, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { uniq } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { DealFilterService } from './deal-filter.service';
import { AppActions } from '../../../store/app/app.actions';
import { DealComparisonActions } from '../../../store/deal-comparison/deal-comparison.actions';
import { DealComparisonSelectors } from '../../../store/deal-comparison/deal-comparison.selectors';
import { DealFilterActions } from '../../../store/deal-filter/deal-filter.actions';
import { DealFilterSelectors } from '../../../store/deal-filter/deal-filter.selectors';
import { RootState } from '../../../store/root-state.model';
import { SUCCESS_NOTIFICATION_CONFIGURATION } from '../../shared/constants/notification-configurations';
import { DealFilter, DealFilterSummary, DealFilterType, QUICK_PORTFOLIO_SET_ID } from '../models/deal-filter.model';

/** The always-include pins, which are the only membership this service can write. */
function isPinned(set: DealFilter, transactionId: string): boolean {
  return !!set.dealList?.includes(transactionId);
}

/** Names one or two portfolios outright, and counts beyond that, so the toast stays one line. */
function summarise(names: string[]): string {
  return names.length <= 2 ? names.join(' and ') : `${names.length} portfolios`;
}

/**
 * Which portfolios a single deal belongs to, editable from anywhere in the app. Membership only:
 * the deal joins or leaves `dealList`, which on a dynamic portfolio is its always-include list, so
 * the criteria are never touched and pin order is never involved.
 */
@Injectable({ providedIn: 'root' })
export class DealPortfolioMembershipService {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly dealFilterService = inject(DealFilterService);

  private readonly sets = this.store.selectSignal(DealFilterSelectors.list);
  private readonly rawSets = this.store.selectSignal(DealFilterSelectors.filters);
  private readonly basketLookup = this.store.selectSignal(DealComparisonSelectors.selectedIdsLookup);
  private readonly basketIds = this.store.selectSignal(DealComparisonSelectors.rawTransactionIds);

  public readonly basketCount = computed(() => this.basketIds()?.length ?? 0);

  /** Every portfolio the user can write, both kinds. */
  public readonly targets = computed<DealFilterSummary[]>(() => (this.sets() ?? []).filter((set) => !set.disabled));

  /**
   * Membership as the rest of the app resolves it: `transactionIds` covers a dynamic portfolio's
   * rule matches as well as its always-include pins, where `dealList` only ever holds the pins.
   */
  public isIn(setId: string, transactionId: string): boolean {
    const set = this.setById(setId);

    return !!set && (isPinned(set, transactionId) || !!set.transactionIds?.[transactionId]);
  }

  /** A member by the portfolio's rules alone, so unticking it here could not remove it. */
  public isRuleOnly(setId: string, transactionId: string): boolean {
    const set = this.setById(setId);

    return !!set && !isPinned(set, transactionId) && !!set.transactionIds?.[transactionId];
  }

  /**
   * The deals a scope holds, for a look before adding to it. A dynamic portfolio's members are
   * resolved server-side, so they are fetched unless the store already holds them.
   */
  public dealIdsIn(setId: string): Observable<string[]> {
    if (setId === QUICK_PORTFOLIO_SET_ID) {
      return this.store.select(DealComparisonSelectors.rawTransactionIds).pipe(map((ids) => ids ?? []));
    }

    const set = this.setById(setId);
    if (!set) {
      return of([]);
    }

    const resolved = Object.keys(set.transactionIds ?? {});
    if (set.type === DealFilterType.Portfolio) {
      return of(uniq([...(set.dealList ?? []), ...resolved]));
    }

    if (resolved.length) {
      return of(resolved);
    }

    return this.dealFilterService.getById(setId).pipe(map((dealFilter) => Object.keys(dealFilter?.transactionIds ?? {})));
  }

  public isInBasket(transactionId: string): boolean {
    return !!this.basketLookup()?.[transactionId];
  }

  /** Private to the user and permission-free, so it is dispatched apart from the portfolio saves. */
  public setBasketMembership(transactionId: string, wanted: boolean): void {
    if (!transactionId || wanted === this.isInBasket(transactionId)) {
      return;
    }

    this.store.dispatch(
      wanted
        ? DealComparisonActions.AddToBasket({ transactionIds: [transactionId] })
        : DealComparisonActions.RemoveFromBasket({ transactionId }),
    );

    this.notifyBasket(wanted);
  }

  /**
   * Applies a whole selection in one dispatch, so the store patches once and the checkboxes never
   * show an intermediate state. Only the portfolios that actually changed are sent.
   */
  public setMembership(transactionId: string, setIds: string[]): void {
    if (!transactionId) {
      return;
    }

    const wanted = new Set(setIds);
    const add: string[] = [];
    const remove: string[] = [];
    const addedNames: string[] = [];
    const removedNames: string[] = [];

    for (const target of this.targets()) {
      const set = this.setById(target.id);
      if (!set) {
        continue;
      }

      const isIn = isPinned(set, transactionId);
      if (wanted.has(target.id) === isIn) {
        continue;
      }

      (isIn ? remove : add).push(set.id);
      (isIn ? removedNames : addedNames).push(set.displayName);
    }

    if (!add.length && !remove.length) {
      return;
    }

    this.store.dispatch(DealFilterActions.SetDealMembership({ transactionId, add, remove }));
    this.notify(addedNames, removedNames);
  }

  private setById(setId: string): DealFilter | undefined {
    return (this.rawSets() ?? []).find((set) => set.id === setId);
  }

  private notifyBasket(added: boolean): void {
    this.store.dispatch(
      AppActions.PushNotification({
        message: added ? 'Added to your basket' : 'Removed from your basket',
        config: SUCCESS_NOTIFICATION_CONFIGURATION,
      }),
    );
  }

  /** One toast for the whole edit: ticking several portfolios should not stack several messages. */
  private notify(added: string[], removed: string[]): void {
    const parts = [added.length ? `Added to ${summarise(added)}` : '', removed.length ? `Removed from ${summarise(removed)}` : ''].filter(
      Boolean,
    );

    if (!parts.length) {
      return;
    }

    this.store.dispatch(AppActions.PushNotification({ message: parts.join('. '), config: SUCCESS_NOTIFICATION_CONFIGURATION }));
  }
}
